/**
 * useImport - Custom hook for CSV import wizard state management
 * 
 * Manages the complete import flow:
 * 1. File upload → 2. Preview/validation → 3. Confirm/execute → 4. Progress/result
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/apiErrors';
import { useLanguage } from '../contexts/LanguageContext';
import { useOrganizationScope } from '../contexts/OrganizationScopeContext';


// Import wizard steps
export const IMPORT_STEPS = {
  UPLOAD: 'upload',
  PREVIEW: 'preview',
  PROGRESS: 'progress',
  RESULT: 'result',
};

// Import status values
export const IMPORT_STATUS = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  PREVIEWING: 'previewing',
  CONFIRMING: 'confirming',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  // Backend-only: an import that ran through but had row errors (skipped invalid rows, failed inserts)
  // ends as completed_with_errors. The hook shows it as COMPLETED; the counts and errors are in result.
  COMPLETED_WITH_ERRORS: 'completed_with_errors',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

// Backend import/job status of an import that ran through, with or without row errors
const isCompletedImportStatus = (status) =>
  status === IMPORT_STATUS.COMPLETED || status === IMPORT_STATUS.COMPLETED_WITH_ERRORS;

// Result shown at the result step, from the result of the confirm or of the job. It carries its own job:
// downloadErrors takes the job of the result shown (the synchronous path never sets job).
const toImportResult = (jobId, result) => ({
  job_id: jobId,
  imported: result?.imported || 0,
  skipped: result?.skipped || 0,
  failed: result?.failed || 0,
  updated: result?.updated || 0,
  errors: result?.errors || [],
});

// The import created or changed records, so the list behind the wizard is out of date
const hasWrittenRows = (result) => (result?.imported || 0) + (result?.updated || 0) > 0;

// Text of one row error ({ row, field, value, code, message }). The backend sends the code in
// SCREAMING_SNAKE_CASE (NEGATIVE_NUMBER), t.import.errors is keyed by its camelCase form (negativeNumber);
// a code without a translation falls back to the backend's (English) message.
export const getImportRowErrorMessage = (t, rowError) => {
  const key = rowError?.code?.toLowerCase().replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  return (key && t.import?.errors?.[key]) || rowError?.message || rowError?.code || '';
};

// Duplicate handling modes (must match backend DuplicateMode enum)
export const DUPLICATE_MODE = {
  SKIP: 'skip',
  UPDATE: 'update',
  FAIL: 'fail',
};

// Error of a refused confirm, shown at the preview step: a key of t.import.confirmErrors, otherwise the
// backend's own text (lib/apiErrors.js). The backend answers with ProblemDetails whose detail is plain
// English: 400 "Preview expired. ..." (also after an API restart — previews live in memory), 409 for
// another import running in the organization, 400 for duplicates in "fail" mode (ImportService.ConfirmAsync).
const getConfirmError = (err, language, failOnDuplicates) => {
  const response = err?.response;
  if (!response) return 'networkError';
  const { status } = response;
  const detail = response.data?.detail;

  if (status === 404 || (status === 400 && typeof detail === 'string' && /preview expired/i.test(detail))) {
    return 'previewExpired';
  }
  if (status === 409) return 'anotherImportInProgress';
  if (status === 403) return 'forbidden';
  if (status >= 500) return 'serverError';
  if (status === 400 && failOnDuplicates) return 'duplicateFound';
  return getApiErrorMessage(err, language, 'confirmFailed');
};

// Max file size: 5MB
const MAX_FILE_SIZE = 5 * 1024 * 1024;

// Max rows allowed
const MAX_ROWS = 5000;

// Poll interval for async jobs (ms)
const POLL_INTERVAL = 1500;

// Failed polls in a row (≈ 15 s) after which the job counts as lost. A single failure (a dropped
// connection, a one-off 500) is only skipped.
const MAX_CONSECUTIVE_POLL_ERRORS = 10;

/**
 * useImport hook
 * 
 * @param {string} entityType - 'pilots' | 'aircraft' | 'batteries' | 'documents'
 * @param {string} organizationId - Target organization ID
 * @param {Function} onComplete - Callback when an import ends (every completed one, a failed one that wrote rows,
 *   a lost job with null — it may have written rows)
 */
export function useImport(entityType, organizationId, onComplete) {
  const { t, language } = useLanguage();
  const { registerScopeChangeGuard } = useOrganizationScope();
  
  // Current step in wizard
  const [step, setStep] = useState(IMPORT_STEPS.UPLOAD);
  
  // Overall status
  const [status, setStatus] = useState(IMPORT_STATUS.IDLE);
  
  // Selected file
  const [file, setFile] = useState(null);
  
  // Preview data from backend
  const [preview, setPreview] = useState(null);
  
  // Job data for async imports
  const [job, setJob] = useState(null);
  
  // Final result
  const [result, setResult] = useState(null);
  
  // Error message
  const [error, setError] = useState(null);
  
  // Import settings
  const [settings, setSettings] = useState({
    duplicateMode: DUPLICATE_MODE.SKIP,
    skipInvalidRows: true,
  });
  
  // Polling refs
  const pollIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);
  // Job the wizard still follows. A poll answered after the dialog was closed, the import cancelled or the
  // job already finished on another poll belongs to a job no longer followed and is dropped.
  const activeJobIdRef = useRef(null);
  // Failed polls in a row of the followed job
  const pollErrorCountRef = useRef(0);
  // Wizard session: reset (closing the dialog, "Import another"), unmount and every new upload start a new
  // one. An upload/preview, confirm or cancel answered for an older session is dropped — its data, its error
  // and its status alike — so it cannot overwrite the wizard the user has moved on to.
  const sessionRef = useRef(0);

  /**
   * Clean up polling on unmount
   */
  useEffect(() => {
    return () => {
      sessionRef.current += 1;
      activeJobIdRef.current = null;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (status !== IMPORT_STATUS.CONFIRMING && status !== IMPORT_STATUS.PROCESSING) {
      return undefined;
    }

    return registerScopeChangeGuard(() => window.confirm(t.import?.scopeChangeConfirm));
  }, [registerScopeChangeGuard, status, t.import?.scopeChangeConfirm]);

  /**
   * Reset the entire import state
   */
  const reset = useCallback(() => {
    // Requests still under way belong to the session that ends here
    sessionRef.current += 1;
    // Stop polling
    activeJobIdRef.current = null;
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    // Stops waiting for a confirm under way; the server finishes that import all the same (OI-092)
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    setStep(IMPORT_STEPS.UPLOAD);
    setStatus(IMPORT_STATUS.IDLE);
    setFile(null);
    setPreview(null);
    setJob(null);
    setResult(null);
    setError(null);
    setSettings({
      duplicateMode: DUPLICATE_MODE.SKIP,
      skipInvalidRows: true,
    });
  }, []);

  /**
   * Validate selected file
   */
  const validateFile = useCallback((selectedFile) => {
    if (!selectedFile) {
      return { valid: false, errorCode: 'invalidFileType' };
    }
    
    // Check file extension
    const fileName = selectedFile.name.toLowerCase();
    if (!fileName.endsWith('.csv')) {
      return { valid: false, errorCode: 'invalidFileType' };
    }
    
    // Check file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      return { valid: false, errorCode: 'fileTooLarge' };
    }
    
    return { valid: true };
  }, []);

  /**
   * Select and validate a file
   */
  const selectFile = useCallback((selectedFile) => {
    const validation = validateFile(selectedFile);
    if (!validation.valid) {
      setError(validation.errorCode);
      return false;
    }
    
    setFile(selectedFile);
    setError(null);
    return true;
  }, [validateFile]);

  /**
   * Upload file and get preview
   */
  const uploadAndPreview = useCallback(async () => {
    if (!file) {
      setError('invalidFileType');
      return false;
    }

    const session = ++sessionRef.current;
    setStatus(IMPORT_STATUS.UPLOADING);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const url = organizationId
        ? `/imports/${entityType}/preview?organization_id=${organizationId}`
        : `/imports/${entityType}/preview`;

      const response = await api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Dialog closed (or another upload started) meanwhile: the preview of an abandoned file is dropped
      if (sessionRef.current !== session) return false;

      const previewData = response.data;
      
      // Normalize the preview data to match component expectations
      // Backend returns: file_info.total_rows, summary.{valid_rows, invalid_rows, duplicate_rows, warnings}, preview_rows
      const normalizedPreview = {
        preview_id: previewData.preview_id,
        expires_at: previewData.expires_at,
        entity_type: previewData.entity_type,
        organization_id: previewData.organization_id,
        // Map file_info
        total_rows: previewData.file_info?.total_rows || 0,
        file_name: previewData.file_info?.file_name,
        encoding: previewData.file_info?.detected_encoding,
        delimiter: previewData.file_info?.detected_delimiter,
        // Map summary
        valid_count: previewData.summary?.valid_rows || 0,
        invalid_count: previewData.summary?.invalid_rows || 0,
        duplicate_count: previewData.summary?.duplicate_rows || 0,
        warning_count: previewData.summary?.warnings || 0,
        // Map rows
        rows: previewData.preview_rows || [],
      };
      
      // Check for too many rows
      if (normalizedPreview.total_rows > MAX_ROWS) {
        setError('tooManyRows');
        setStatus(IMPORT_STATUS.IDLE);
        return false;
      }

      setPreview(normalizedPreview);
      setStep(IMPORT_STEPS.PREVIEW);
      setStatus(IMPORT_STATUS.PREVIEWING);
      return true;

    } catch (err) {
      if (sessionRef.current !== session) return false;

      const detail = err.response?.data?.detail;
      
      if (typeof detail === 'object' && detail.code) {
        setError(detail.code);
      } else if (typeof detail === 'string') {
        // Try to parse known error patterns
        if (detail.includes('encoding')) {
          setError('encodingError');
        } else if (detail.includes('forbidden') || err.response?.status === 403) {
          setError('forbidden');
        } else {
          setError('uploadError');
        }
      } else {
        setError('uploadError');
      }
      
      setStatus(IMPORT_STATUS.FAILED);
      return false;
    }
  }, [file, entityType, organizationId]);

  /**
   * Poll job status for async imports
   */
  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const response = await api.get(`/imports/jobs/${jobId}`, {
        
      });

      // Answered after reset/cancelImport or after another poll finished the job: not followed any more
      if (activeJobIdRef.current !== jobId) return;

      pollErrorCountRef.current = 0;
      const jobData = response.data;
      setJob(jobData);

      // Check if job completed (row errors included)
      if (isCompletedImportStatus(jobData.status)) {
        activeJobIdRef.current = null;
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        
        setResult(toImportResult(jobId, jobData.result));
        setStep(IMPORT_STEPS.RESULT);
        setStatus(IMPORT_STATUS.COMPLETED);
        
        if (onComplete) {
          onComplete(jobData.result);
        }
        return;
      }

      // Check if job failed. A job a server error stopped part-way (OI-092) ends failed with its result: the
      // rows written before the failure stay, so their counts and errors are shown, and the list is refreshed
      // if there are any.
      if (jobData.status === IMPORT_STATUS.FAILED) {
        activeJobIdRef.current = null;
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        
        if (jobData.result) {
          setResult(toImportResult(jobId, jobData.result));
        } else {
          setError(jobData.error || 'serverError');
        }
        setStep(IMPORT_STEPS.RESULT);
        setStatus(IMPORT_STATUS.FAILED);

        if (onComplete && hasWrittenRows(jobData.result)) {
          onComplete(jobData.result);
        }
        return;
      }

      // Check if job cancelled
      if (jobData.status === 'cancelled') {
        activeJobIdRef.current = null;
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        
        setStep(IMPORT_STEPS.RESULT);
        setStatus(IMPORT_STATUS.CANCELLED);
        return;
      }

    } catch (err) {
      if (activeJobIdRef.current !== jobId) return;

      // 404: the job is gone — jobs live only in the API's memory, so a restart or redeploy loses them.
      // Any other error is skipped as transient until it repeats too often.
      const jobLost = err.response?.status === 404;
      if (!jobLost && ++pollErrorCountRef.current < MAX_CONSECUTIVE_POLL_ERRORS) {
        console.error('Poll error:', err);
        return;
      }

      activeJobIdRef.current = null;
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;

      // How the job ended is unknown: rows written before it was lost (or still being written) stay, so
      // the message asks to check the list, and the list is refreshed.
      setError(jobLost ? 'serverRestart' : 'jobStatusUnavailable');
      setStep(IMPORT_STEPS.RESULT);
      setStatus(IMPORT_STATUS.FAILED);

      if (onComplete) {
        onComplete(null);
      }
    }
  }, [onComplete]);

  /**
   * Confirm and execute import
   */
  const confirmImport = useCallback(async () => {
    if (!preview?.preview_id) {
      setError('previewExpired');
      return false;
    }

    const session = sessionRef.current;
    setStatus(IMPORT_STATUS.CONFIRMING);
    setError(null);

    try {
      abortControllerRef.current = new AbortController();

      const params = new URLSearchParams({
        preview_id: preview.preview_id,
        duplicate_mode: settings.duplicateMode,
        skip_invalid_rows: settings.skipInvalidRows.toString(),
      });

      const response = await api.post(
        `/imports/${entityType}/confirm?${params.toString()}`,
        {},
        {
          signal: abortControllerRef.current.signal,
        }
      );

      // reset/unmount abort the request; an answer that got through anyway is still the old session's
      if (sessionRef.current !== session) return false;

      const importResponse = response.data;

      // Check if synchronous result (≤100 rows), row errors included
      if (isCompletedImportStatus(importResponse.status)) {
        setResult(toImportResult(importResponse.job_id, importResponse.result));
        setStep(IMPORT_STEPS.RESULT);
        setStatus(IMPORT_STATUS.COMPLETED);
        
        if (onComplete) {
          onComplete(importResponse.result);
        }
        return true;
      }

      // Synchronous import a server error stopped part-way (OI-092): it ran, and the rows written before the
      // failure stay. Its result ends the wizard like that of a completed import, so the preview it used up is
      // not offered for confirming again (which wrote the same rows twice); the list is refreshed if rows were
      // written.
      if (importResponse.status === IMPORT_STATUS.FAILED && importResponse.result) {
        setResult(toImportResult(importResponse.job_id, importResponse.result));
        setStep(IMPORT_STEPS.RESULT);
        setStatus(IMPORT_STATUS.FAILED);

        if (onComplete && hasWrittenRows(importResponse.result)) {
          onComplete(importResponse.result);
        }
        return true;
      }

      // Async job started
      if (importResponse.status === 'processing' && importResponse.job_id) {
        setJob({
          job_id: importResponse.job_id,
          status: 'processing',
          progress: 0,
        });
        setStep(IMPORT_STEPS.PROGRESS);
        setStatus(IMPORT_STATUS.PROCESSING);

        // Start polling
        activeJobIdRef.current = importResponse.job_id;
        pollErrorCountRef.current = 0;
        pollIntervalRef.current = setInterval(() => {
          pollJobStatus(importResponse.job_id);
        }, POLL_INTERVAL);

        return true;
      }

      // Unexpected response
      setError('serverError');
      setStatus(IMPORT_STATUS.FAILED);
      return false;

    } catch (err) {
      if (api.isCancel?.(err) || err.code === 'ERR_CANCELED' || sessionRef.current !== session) {
        return false;
      }

      // The step stays the preview, which shows the error; the button is enabled again
      const failOnDuplicates = settings.duplicateMode === DUPLICATE_MODE.FAIL && preview.duplicate_count > 0;
      setError(getConfirmError(err, language, failOnDuplicates));
      setStatus(IMPORT_STATUS.FAILED);
      return false;
    }
  }, [preview, settings, entityType, language, onComplete, pollJobStatus]);

  /**
   * Cancel ongoing import job
   */
  const cancelImport = useCallback(async () => {
    const session = sessionRef.current;
    // Abort any pending requests (a poll still under way is dropped when it answers)
    activeJobIdRef.current = null;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Stop polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // If we have a job, tell backend to cancel
    if (job?.job_id) {
      try {
        await api.delete(`/imports/jobs/${job.job_id}`, {
          
        });
      } catch (err) {
        // Ignore cancel errors
        console.error('Cancel error:', err);
      }
    }

    // Dialog closed while the DELETE was under way: the wizard has moved on (maybe to the next import)
    if (sessionRef.current !== session) return;

    setStatus(IMPORT_STATUS.CANCELLED);
    setStep(IMPORT_STEPS.RESULT);
  }, [job]);

  /**
   * Go back to previous step
   */
  const goBack = useCallback(() => {
    if (step === IMPORT_STEPS.PREVIEW) {
      setStep(IMPORT_STEPS.UPLOAD);
      setStatus(IMPORT_STATUS.IDLE);
      setPreview(null);
      // Error of a refused confirm belongs to the preview; the file stays picked for a new upload
      setError(null);
    }
  }, [step]);

  /**
   * Update import settings
   */
  const updateSettings = useCallback((newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
    // The error of a refused confirm was for the previous settings
    setError(null);
  }, []);

  /**
   * Download error CSV
   */
  const downloadErrors = useCallback(async () => {
    // The job of the result shown; job is only the async progress
    const jobId = result?.job_id || job?.job_id;
    if (!jobId) return;

    try {
      const response = await api.get(`/imports/jobs/${jobId}/errors`, {
        
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `import_errors_${jobId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download errors failed:', err);
    }
  }, [job, result]);

  /**
   * Download CSV template
   */
  const downloadTemplate = useCallback(async (lang = 'en') => {
    try {
      const response = await api.get(
        `/imports/templates/${entityType}?lang=${lang}`,
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${entityType}_template_${lang}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download template failed:', err);
    }
  }, [entityType]);

  // Computed values
  const canConfirm = preview && (
    preview.valid_count > 0 || 
    (settings.skipInvalidRows && preview.valid_count > 0) ||
    (!settings.skipInvalidRows && preview.invalid_count === 0)
  );

  // Rows the import writes: valid ones (warnings included) and, with "Update existing", the duplicates it
  // updates — the preview puts every row in exactly one of valid/invalid/duplicate, so none counts twice.
  // Invalid rows not skipped: every row is sent (the invalid ones fail).
  const importableCount = settings.skipInvalidRows
    ? (preview?.valid_count || 0) +
      (settings.duplicateMode === DUPLICATE_MODE.UPDATE ? preview?.duplicate_count || 0 : 0)
    : preview?.total_rows || 0;

  return {
    // State
    step,
    status,
    file,
    preview,
    job,
    result,
    error,
    settings,
    
    // Computed
    canConfirm,
    importableCount,
    
    // Actions
    reset,
    selectFile,
    uploadAndPreview,
    confirmImport,
    cancelImport,
    goBack,
    updateSettings,
    downloadErrors,
    downloadTemplate,
  };
}

export default useImport;
